import ContextualSaveBar from "@/components/bars/ContextualSaveBar";
import PageLoader from "@/components/loader/PageLoader";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

interface Option {
  id: number;
  name: string;
  description: string;
  color: string;
  surcharge: string;
}

const EditOption = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { id: optionId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [option, setOption] = useState<Option>({
    id: 0,
    name: "",
    description: "",
    color: "#ffffff",
    surcharge: "",
  });
  const [originalOption, setOriginalOption] = useState<Option>({
    id: 0,
    name: "",
    description: "",
    color: "#ffffff",
    surcharge: "",
  });

  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const colorOptions = [
    { label: "Red", value: "#ff0000" },
    { label: "Green", value: "#00ff00" },
    { label: "Blue", value: "#0000ff" },
    { label: "Yellow", value: "#ffff00" },
    { label: "Purple", value: "#800080" },
    { label: "Orange", value: "#ffa500" },
    { label: "Black", value: "#000000" },
  ];

  const handleColorChange = (value: string) => {
    if (value === "") {
      setOption({ ...option, color: "#ffffff" });
    } else {
      setOption({ ...option, color: value });
    }
  };

  useEffect(() => {
    const fetchOption = async () => {
      if (loading || !axiosInstance || !optionId) return;
      try {
        const apiUrl =
          import.meta.env.VITE_APP_REDBIRDPOSBE_OPTION_INFORMATION ?? "";
        const response = await axiosInstance.get(`${apiUrl}${optionId}/`);
        setOption(response.data);
        setOriginalOption(response.data);
      } catch (error) {
        console.error("Error fetching option:", error);
        toast.error("Failed to fetch option details.");
      }
    };

    if (optionId) {
      fetchOption();
    }
  }, [axiosInstance, loading, optionId]);

  useEffect(() => {
    if (originalOption) {
      const isChanged = !_.isEqual(option, originalOption);
      setIsDirty(isChanged);
    }
  }, [option, originalOption]);

  const handleSubmit = async () => {
    if (loading || !axiosInstance || !optionId) return;
    const optionData: {
      branch: string | undefined;
      name: string;
      description: string;
      color: string;
      surcharge: string;
    } = {
      branch: user?.selectedBranch?.id.toString(),
      name: option.name,
      description: option.description,
      color: option.color,
      surcharge: option.surcharge || "0",
    };

    try {
      const apiUrl =
        import.meta.env.VITE_APP_REDBIRDPOSBE_OPTION_INFORMATION ?? "";
      await axiosInstance.put(`${apiUrl}${optionId}/`, optionData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      toast.success(t("back.management.menu.option.successupdate"));
      navigate("/menu/options");
    } catch (error) {
      console.error("Error updating option:", error);
      toast.error(t("back.management.menu.option.failupdate"));
    }
  };

  const handleDiscard = () => {
    if (originalOption) {
      setOption(originalOption);
      setIsDirty(false);
    }
  };

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto px-4 py-2">
        <h1 className="text-xl font-bold">
          {t("back.management.menu.option.editOption")}
        </h1>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">
              {t("back.management.menu.option.generalinfo")}
            </h2>
            <div className="-mx-2 flex flex-wrap">
              <div className="mb-4 w-full px-2 md:mb-0 md:w-1/2">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="name"
                >
                  {t("back.management.menu.option.optionName")}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  placeholder={t("back.management.menu.option.enterOptionName")}
                  value={option.name}
                  onChange={(e) =>
                    setOption({ ...option, name: e.target.value })
                  }
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                />
              </div>
              <div className="w-full px-2 md:w-1/2">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="color"
                >
                  {t("back.management.menu.option.optionColor")}
                </label>
                <select
                  id="color"
                  value={option.color}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                >
                  <option value="">
                    {t("back.management.menu.option.selectColor")}
                  </option>
                  {colorOptions.map((colorOption) => (
                    <option key={colorOption.value} value={colorOption.value}>
                      {colorOption.label}
                    </option>
                  ))}
                </select>
                {option.color && (
                  <FontAwesomeIcon
                    icon={faCircle}
                    color={option.color}
                    className="mt-2"
                  />
                )}
              </div>
            </div>
            <div className="mt-4">
              <label
                className="mb-2 block text-sm font-bold text-gray-700"
                htmlFor="description"
              >
                {t("back.management.menu.option.description")}
              </label>
              <textarea
                id="description"
                placeholder={t("back.management.menu.option.enterDescription")}
                value={option.description}
                onChange={(e) =>
                  setOption({ ...option, description: e.target.value })
                }
                className="block h-48 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
              />
            </div>
          </div>

          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">
              {t("back.management.menu.option.surcharge")}
            </h2>
            <input
              type="text"
              placeholder={t("back.management.menu.option.enterSurcharge")}
              value={option.surcharge}
              onChange={(e) =>
                setOption({ ...option, surcharge: e.target.value })
              }
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
            />
          </div>

          <div className="mt-4 flex justify-end">
            <Link
              to="/menu/options"
              className="mr-2 rounded-md bg-gray-500 px-4 py-2 text-white"
            >
              {t("back.management.menu.option.cancelbutton")}
            </Link>
            <button
              type="submit"
              className="rounded-md bg-red-500 px-4 py-2 text-white"
            >
              {t("back.management.menu.option.updatebutton")}
            </button>
          </div>
        </form>
      </div>
      <ContextualSaveBar
        isDirty={isDirty}
        isSaving={isSaving}
        onSave={handleSubmit}
        onDiscard={handleDiscard}
      />
    </PageLoader>
  );
};

export default EditOption;
